import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  projectsApi,
  type ProjectDetail,
  type ProjectInvitationSummary,
  type CreateProjectData,
} from '@/lib/api'
import { useToast } from '@/hooks/ui/use-toast'

/**
 * Mutation for creating a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: CreateProjectData) => {
      const response = await projectsApi.create(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast({
        title: 'Success!',
        description: 'Project posted successfully!',
      })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create project'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })
}

/**
 * Mutation for cancelling an existing project
 */
export function useCancelProject() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (projectId: number): Promise<ProjectDetail> => {
      const response = await projectsApi.cancel(projectId)
      return response.data
    },
    onSuccess: (project) => {
      queryClient.setQueryData(['project', project.id], project)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', 'customer-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['projects', 'developer-dashboard'] })
      toast({
        title: 'Success!',
        description: 'Project cancelled',
      })
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Unable to cancel project',
      })
    },
  })
}

/**
 * Mutation for inviting a developer to a project
 */
export function useInviteDeveloper() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      projectId,
      developerId,
      message,
    }: {
      projectId: number
      developerId: number
      message?: string
    }): Promise<ProjectInvitationSummary> => {
      const response = await projectsApi.invite(projectId, {
        developer_id: developerId,
        message,
      })
      return response.data
    },
    onSuccess: (invitation, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['project', variables.projectId],
      })
      queryClient.invalidateQueries({
        queryKey: ['projects', 'customer-dashboard'],
      })
      toast({
        title: 'Success!',
        description: `Invite sent to ${invitation.developer_username}`,
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.developer_id ?? error?.response?.data?.detail
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message ?? 'Unable to send invite',
      })
    },
  })
}
